import type { ReactNode } from "react";

export type ServiceIconName =
  | "cpu"
  | "memory"
  | "drive"
  | "network"
  | "pcie"
  | "plug"
  | "power"
  | "hardware"
  | "server"
  | "shield"
  | "storage"
  | "terminal"
  | "rocket";

type ServiceIconProps = {
  name: ServiceIconName;
  tone: "blue" | "green" | "amber" | "cyan" | "violet" | "rose";
};

const iconPaths: Record<ServiceIconName, ReactNode> = {
  cpu: (
    <>
      <rect x="8" y="8" width="16" height="16" rx="3" />
      <rect x="12" y="12" width="8" height="8" rx="1" />
      <path d="M4 10h4M4 16h4M4 22h4M24 10h4M24 16h4M24 22h4M10 4v4M16 4v4M22 4v4M10 24v4M16 24v4M22 24v4" />
    </>
  ),
  memory: (
    <>
      <rect x="5" y="10" width="22" height="12" rx="3" />
      <path d="M9 10V7M13 10V7M19 10V7M23 10V7M9 25v-3M13 25v-3M19 25v-3M23 25v-3M10 15h2M15 15h2M20 15h2M10 18h12" />
    </>
  ),
  drive: (
    <>
      <path d="M8 8h16l3 9v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7l3-9Z" />
      <path d="M7 17h18M11 22h7" />
      <circle cx="22" cy="22" r="1.5" />
    </>
  ),
  network: (
    <>
      <rect x="13" y="4" width="6" height="6" rx="1.5" />
      <rect x="4" y="22" width="6" height="6" rx="1.5" />
      <rect x="22" y="22" width="6" height="6" rx="1.5" />
      <path d="M16 10v6M7 22v-3a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v3" />
    </>
  ),
  pcie: (
    <>
      <rect x="7" y="6" width="18" height="20" rx="3" />
      <circle cx="13" cy="13" r="2" />
      <circle cx="19" cy="19" r="2" />
      <path d="M15 13h4v4M11 24h10M11 9h10" />
    </>
  ),
  plug: (
    <>
      <path d="M11 4v7M21 4v7M9 11h14v5a7 7 0 0 1-14 0v-5ZM16 23v5" />
      <path d="M22 24c3 0 4-2 4-4v-3" />
      <circle cx="26" cy="15" r="2" />
    </>
  ),
  power: (
    <>
      <path d="M16 4v12" />
      <path d="M10 7.5a10 10 0 1 0 12 0" />
    </>
  ),
  hardware: (
    <>
      <rect x="8" y="7" width="16" height="18" rx="3" />
      <path d="M12 11h8M12 15h8M12 19h8" />
      <path d="M5 12H3M5 20H3M29 12h-2M29 20h-2" />
    </>
  ),
  server: (
    <>
      <rect x="6" y="7" width="20" height="8" rx="2.5" />
      <rect x="6" y="18" width="20" height="8" rx="2.5" />
      <path d="M11 11h8M11 22h8" />
      <circle cx="22" cy="11" r="1.2" />
      <circle cx="22" cy="22" r="1.2" />
    </>
  ),
  shield: (
    <>
      <path d="M16 4 26 8v7c0 6-4 10-10 13C10 25 6 21 6 15V8l10-4Z" />
      <path d="m11 16 3 3 7-7" />
    </>
  ),
  storage: (
    <>
      <ellipse cx="16" cy="8" rx="10" ry="4" />
      <path d="M6 8v8c0 2.2 4.5 4 10 4s10-1.8 10-4V8" />
      <path d="M6 16v8c0 2.2 4.5 4 10 4s10-1.8 10-4v-8" />
    </>
  ),
  terminal: (
    <>
      <rect x="5" y="7" width="22" height="18" rx="3" />
      <path d="m10 13 4 3-4 3M16 20h6" />
    </>
  ),
  rocket: (
    <>
      <path d="M17 4c5 1 8 4 9 9l-7 7c-2-1-4-3-7-7l5-9Z" />
      <path d="M12 13 7 15l4 2M19 20l-2 5-2-4M9 23l-2 2" />
      <circle cx="19" cy="11" r="2" />
    </>
  ),
};

export function ServiceIcon({ name, tone }: ServiceIconProps) {
  return (
    <span className={`magic-icon-badge magic-icon-${tone}`} aria-hidden="true">
      <svg viewBox="0 0 32 32" fill="none">
        {iconPaths[name]}
      </svg>
    </span>
  );
}
